import { useState, useEffect, useCallback } from 'react';
import { Moon, LogOut, Loader2, AlertTriangle, ChevronRight, ShieldCheck } from 'lucide-react';
import SortersAdminPage from './SortersAdminPage';
import PeakAvailabilityView from './PeakAvailabilityView';

// ── Night Sort Roster — scoped manager surface ────────────────────────────────
// A standalone page (outside the admin <Layout>) that lets an allowlisted night-sort
// roster manager (e.g. Valeria / ASTORGACV) sign in with her manager access code and build
// the weekly roster — and ONLY that. She never sees the rest of the back-office. Her saves
// are drafts submitted for admin approval (see SortersAdminPage draftMode / sort-roster-
// draft-sb). Auth: manager-auth.js sets the signed hp_manager cookie; the roster-manager
// allowlist is enforced server-side, re-checked here via whoamiRoster for a clean message.

const SESSION_KEY = 'hp_roster_session';

const S = {
  page: { minHeight: '100vh', background: 'var(--hp-black)', color: 'var(--hp-text)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body)' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--hp-border)', background: 'var(--hp-surface)' },
  brand: { fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', color: 'var(--hp-gold)' },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
  input: { width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius, 8px)', color: 'var(--hp-text)', fontSize: '1rem', padding: '12px 14px', marginBottom: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' },
  bigBtn: (bg) => ({ width: '100%', padding: '16px', background: bg, color: 'var(--hp-black)', fontWeight: 800, fontSize: '1rem', border: 'none', borderRadius: 'var(--radius-lg, 14px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }),
  ghostBtn: { padding: '10px 14px', background: 'transparent', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' },
  card: { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 14px)', padding: '22px 20px', marginBottom: 16 },
  sectionTitle: { fontFamily: 'var(--font-display)', fontSize: '1.15rem', letterSpacing: '0.03em', margin: '0 0 4px' },
  sub: { fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '0 0 16px', lineHeight: 1.5 },
  pill: (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }),
};

// Friendly display names for the known roster managers (whoamiRoster only returns the login).
const KNOWN_NAMES = { ASTORGACV: 'Valeria Astorga' };

export default function SortRosterPortalPage() {
  const [session, setSession] = useState(null);          // { login, name, isAdmin }
  const [checking, setChecking] = useState(true);        // verifying an existing cookie on mount
  const [form, setForm] = useState({ login: '', code: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Roster | Peak availability (read-only team calendar, peak-availability-sb PEAK_VIEWERS).
  // The roster stays mounted while hidden so an unsaved week isn't lost by switching.
  const [view, setView] = useState('roster');

  const post = useCallback((action, payload = {}) => fetch('/.netlify/functions/appsscript', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
  }).then(r => r.json()), []);

  // On mount, ask the server who the hp_manager cookie belongs to. If it's a roster manager
  // (or admin), skip the login form — the cookie is still valid.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const who = await post('whoamiRoster');
        if (live && who && who.success && who.isManager && who.login) {
          let name = KNOWN_NAMES[who.login] || who.login;
          try { const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); if (s && s.login === who.login && s.name) name = s.name; } catch {}
          setSession({ login: who.login, name, isAdmin: !!who.isAdmin });
        }
      } catch {}
      if (live) setChecking(false);
    })();
    return () => { live = false; };
  }, [post]);

  async function handleLogin() {
    setError(''); setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/manager-auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: form.login, code: form.code }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Invalid login or access code.');
      // Signed in as a manager — confirm this login is authorised for the roster.
      const who = await post('whoamiRoster');
      if (!who || !who.success || !who.isManager) {
        throw new Error('Signed in, but this login is not set up for the roster yet. Ask the office to enable it.');
      }
      const name = data.name || KNOWN_NAMES[(data.login || '').toUpperCase()] || data.login;
      const sess = { login: (data.login || '').toUpperCase(), name, isAdmin: !!who.isAdmin };
      localStorage.setItem(SESSION_KEY, JSON.stringify(sess));
      setSession(sess);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function logout() { localStorage.removeItem(SESSION_KEY); setSession(null); setForm({ login: '', code: '' }); }

  if (checking) {
    return (
      <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={26} color="var(--hp-gold)" style={{ animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (!session) {
    return (
      <div style={S.page}>
        <div style={S.header}><Moon size={22} color="var(--hp-gold)" /><span style={S.brand}>Hollypaul · Night Sort Roster</span></div>
        <div style={{ width: '100%', maxWidth: 420, margin: '0 auto', padding: '20px 16px 60px' }}>
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Sign in</h2>
            <p style={S.sub}>Use the roster login and access code the office gave you.</p>
            <label style={S.label}>Login</label>
            <input style={S.input} value={form.login} autoCapitalize="characters" autoComplete="username"
              onChange={e => setForm(f => ({ ...f, login: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="e.g. ASTORGACV" />
            <label style={S.label}>Access code</label>
            <input style={S.input} type="password" autoComplete="current-password" value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="••••••" />
            {error && <div style={{ ...S.pill('rgba(239,68,68,0.12)', 'var(--hp-red, #EF4444)'), marginBottom: 14, padding: '8px 12px' }}><AlertTriangle size={13} />{error}</div>}
            <button style={S.bigBtn('var(--hp-gold)')} disabled={busy} onClick={handleLogin}>
              {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <>Sign in <ChevronRight size={18} /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <Moon size={22} color="var(--hp-gold)" />
        <div style={{ flex: 1 }}>
          <div style={S.brand}>Night Sort Roster</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={12} /> {session.name}{session.isAdmin ? ' · admin' : ' · roster manager'}
          </div>
        </div>
        <button style={S.ghostBtn} onClick={logout}><LogOut size={14} /> Sign out</button>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '12px 20px 0', flexWrap: 'wrap' }}>
        {[['roster', 'Roster'], ['peak', 'Peak availability']].map(([v, lbl]) => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${view === v ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: view === v ? 'rgba(201,168,76,0.12)' : 'transparent', color: view === v ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>{lbl}</button>
        ))}
      </div>
      <div style={{ flex: 1, display: view === 'roster' ? 'block' : 'none' }}>
        <SortersAdminPage rosterOnly draftMode managerName={session.name} />
      </div>
      {view === 'peak' && <div style={{ padding: '16px 20px', maxWidth: 1100 }}><PeakAvailabilityView /></div>}
    </div>
  );
}
